/**
 * Extract error message from various error formats
 * Handles string, array (Pydantic validation), and object errors
 */
export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  if (!error) return defaultMessage;

  // Use pre-set user-friendly message if available (from api.js interceptor)
  if (error.userMessage) {
    return error.userMessage;
  }

  // Handle specific status codes
  if (error.response?.status === 520) {
    return 'Connection issue. Please try again.';
  }
  
  if (error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504) {
    return 'Server is temporarily unavailable. Please try again in a moment.';
  }

  // Network error (no response)
  if (!error.response || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return 'Network connection error. Please check your internet connection and try again.';
  }

  // Timeout error
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please check your connection and try again.';
  }

  // Check for response data detail (most common case)
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    
    // String error message
    if (typeof detail === 'string') {
      return detail;
    }
    
    // Array of errors (Pydantic validation errors)
    if (Array.isArray(detail)) {
      return detail.map(err => {
        if (typeof err === 'string') return err;
        if (err.msg) return err.msg;
        if (err.message) return err.message;
        return JSON.stringify(err);
      }).join(', ');
    }
    
    // Object error
    if (typeof detail === 'object') {
      if (detail.message) return detail.message;
      if (detail.msg) return detail.msg;
      return JSON.stringify(detail);
    }
  }

  // Check for response data message
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Check for standard error message
  if (error.message) {
    return error.message;
  }

  // Fallback
  return defaultMessage;
};

/**
 * Show toast notification with proper error handling
 */
export const showErrorToast = (toast, error, defaultMessage = 'An error occurred') => {
  const errorMessage = getErrorMessage(error, defaultMessage);
  
  toast({
    title: 'Error',
    description: errorMessage,
    variant: 'destructive',
  });
};

/**
 * Show success toast notification
 */
export const showSuccessToast = (toast, title, description) => {
  toast({
    title,
    description,
  });
};
